import type { Buyer, BuyerPatch } from "./contracts";
import { regionFits } from "./countries";
import { readPhone } from "./phone";

/**
 * A saved profile after a change (`updateBuyer`): each detail the change
 * gives replaces the saved one, and null forgets it; every other detail is
 * kept, as a checkout that asks for some of them (DICE's postcode and
 * country) leaves the rest alone. A form with one box for the street (an app
 * build from before the second line) gives the whole street there: a new
 * street replaces both lines, and the same street keeps both. A region is
 * saved with its country's code ("US-CA"), so one kept across a change of
 * country is never read as the new country's. A part of an address another country's
 * addresses do not take is dropped from what a seller is given
 * (`buyerInCountry`), and one they take differently is asked for again by
 * the next form that asks for it.
 *
 * - A phone given is saved E.164 with the country it is dialled in, read
 *   in the country the change names for it, else the address country (an
 *   app build's form names none: its numbers were always read there). The
 *   saved number given back with no country, in any form, keeps its
 *   meaning and its dialling country. One that cannot be read is kept as
 *   typed, read in the address country later as before.
 * - A birth year and a full date describe the same birthday. Changing one
 *   forgets the other if it no longer agrees, including a hidden detail
 *   returned unchanged by an older form. A changed full date takes precedence
 *   when both change; clearing either forgets the related saved detail.
 * - A phone saved before phones were saved E.164 was read in the address
 *   country: when the address moves to another country, it is saved E.164
 *   first, so it keeps its meaning.
 */
export function nextBuyerDetails(saved: Partial<Buyer>, patch: BuyerPatch): Partial<Buyer> {
  const next: Partial<Record<keyof Buyer, string>> = { ...saved };
  const moved = patch.country !== undefined && saved.country !== undefined && patch.country !== saved.country;
  if (moved && saved.phone && !saved.phoneCountry && patch.phone === undefined) {
    const dialled = readPhone(saved.phone, saved.country);
    if (dialled) {
      next.phone = dialled.e164;
      next.phoneCountry = dialled.country;
    }
  }
  if (typeof patch.address === "string" && patch.address2 === undefined && patch.address !== saved.address) delete next.address2;
  for (const [key, value] of Object.entries(patch) as [keyof Buyer, string | null | undefined][]) {
    if (value === null) delete next[key];
    else if (value !== undefined) next[key] = value;
  }
  if (patch.dateOfBirth !== undefined && patch.dateOfBirth !== saved.dateOfBirth) {
    if (!next.dateOfBirth || next.birthYear !== next.dateOfBirth.slice(0, 4)) delete next.birthYear;
  } else if (patch.birthYear !== undefined && patch.birthYear !== saved.birthYear) {
    if (!next.birthYear || next.dateOfBirth?.slice(0, 4) !== next.birthYear) delete next.dateOfBirth;
  }
  if (patch.phone) {
    // A build with no phone country (an app build from before this) that
    // gives back the saved number, in any form, keeps its meaning and its
    // dialling country: read where it was read before, not in a new address
    // country (+1 is still Canadian when its owner moves to the UK, and a
    // UK 07700 900123 stays British however often it is given back).
    const savedIn = saved.phoneCountry ?? saved.country;
    const before = saved.phone ? readPhone(saved.phone, savedIn) : null;
    const same = patch.phoneCountry === undefined && before && readPhone(patch.phone, savedIn)?.e164 === before.e164;
    const dialled = same ? before : readPhone(patch.phone, patch.phoneCountry ?? next.country);
    if (dialled) {
      next.phone = dialled.e164;
      next.phoneCountry = dialled.country;
    } else if (patch.phoneCountry === undefined) delete next.phoneCountry;
  }
  return next as Partial<Buyer>;
}

/**
 * A saved profile's details as a vendor's checkout is given them, in its
 * country's terms: a region its addresses do not have, or one it does not
 * list (one left from another country), dropped, and the phone E.164 with
 * the country it is dialled in, a phone saved before then read in the
 * address country. A postcode is kept whatever the country: the sellers'
 * forms require one.
 */
export function buyerInCountry<T extends Partial<Buyer>>(buyer: T): T {
  const { region: _region, ...rest } = buyer;
  const dialled = buyer.phone ? readPhone(buyer.phone, buyer.phoneCountry ?? buyer.country) : null;
  return {
    ...rest,
    ...(buyer.region !== undefined && regionFits(buyer.country, buyer.region) ? { region: buyer.region } : {}),
    ...(dialled ? { phone: dialled.e164, phoneCountry: dialled.country } : {}),
  } as T;
}
